/**
 * Advances — Vue globale acomptes (parité BE-TAYE AdvancesList.jsx).
 * Workflow pending → approved → paid, avec injection auto en paie.
 */
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { DatePickerFR } from "@/components/ui/date-picker-fr";
import {
  Wallet, Check, X, Clock, Plus, CircleDollarSign, Trash2, CheckCheck,
} from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  pending:  { cls: "bg-amber-100 text-amber-700 border-amber-200", label: "À valider", icon: Clock },
  approved: { cls: "bg-blue-100 text-blue-700 border-blue-200", label: "Validé", icon: Check },
  paid:     { cls: "bg-emerald-100 text-emerald-700 border-emerald-200", label: "Payé", icon: CheckCheck },
  rejected: { cls: "bg-red-100 text-red-700 border-red-200", label: "Refusé", icon: X },
};

const fmtEUR = (v) => v == null ? "—" : `${Number(v).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const fmtPeriodFR = (p) => {
  if (!p || p.length !== 7) return p || "—";
  const [y, m] = p.split("-");
  const months = ["janv.", "févr.", "mars", "avr.", "mai", "juin",
                    "juil.", "août", "sept.", "oct.", "nov.", "déc."];
  return `${months[parseInt(m, 10) - 1]} ${y}`;
};

function NewAdvanceDialog({ open, onOpenChange, onSaved }) {
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState({
    employee_id: "", amount: "", requested_date: new Date().toISOString().slice(0, 10),
    deduction_period: "", reason: "",
  });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) api.get("/hr/employees").then(r => setEmployees(r.data.employees || []))
      .catch(e => toast.error(apiError(e)));
  }, [open]);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.employee_id || !form.amount) { toast.error("Salarié et montant requis"); return; }
    setSaving(true);
    try {
      const { employee_id, ...body } = form;
      body.amount = parseFloat(body.amount);
      await api.post(`/hr/employees/${employee_id}/advances`, body);
      toast.success("Demande d'acompte créée");
      onSaved?.(); onOpenChange(false);
      setForm({ employee_id: "", amount: "", requested_date: new Date().toISOString().slice(0, 10),
                 deduction_period: "", reason: "" });
    } catch (e2) { toast.error(apiError(e2)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="new-advance-dialog">
        <DialogHeader><DialogTitle>Nouvelle demande d'acompte</DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div><Label>Salarié *</Label>
            <Select value={form.employee_id} onValueChange={(v) => setForm(p => ({ ...p, employee_id: v }))}>
              <SelectTrigger data-testid="adv-employee-select"><SelectValue placeholder="Choisir..." /></SelectTrigger>
              <SelectContent>{employees.map(e => (
                <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>
              ))}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Montant (€) *</Label>
              <Input type="number" step="0.01" min="0" value={form.amount}
                onChange={(e) => setForm(p => ({ ...p, amount: e.target.value }))}
                data-testid="adv-amount" />
            </div>
            <div><Label>Date de demande</Label>
              <DatePickerFR value={form.requested_date} onChange={(v) => setForm(p => ({ ...p, requested_date: v }))} />
            </div>
          </div>
          <div><Label>Retenue sur bulletin (YYYY-MM)</Label>
            <Input value={form.deduction_period} onChange={(e) => setForm(p => ({ ...p, deduction_period: e.target.value }))}
              placeholder="Auto : mois suivant" className="font-mono" data-testid="adv-deduction-period" />
          </div>
          <div><Label>Motif</Label>
            <Input value={form.reason} onChange={(e) => setForm(p => ({ ...p, reason: e.target.value }))}
              placeholder="Ex: dépenses de rentrée..." />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
            <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="save-advance-btn">
              {saving ? "..." : "Créer"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Advances() {
  const { hasPermission } = useAuth();
  const canWrite = hasPermission("hr.write") || hasPermission("admin");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");
  const [open, setOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (status !== "all") params.status = status;
      const r = await api.get("/hr/advances", { params });
      setRows(r.data.advances || []);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const approve = async (id) => {
    try { await api.post(`/hr/advances/${id}/approve`); toast.success("Acompte validé"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const reject = async (id) => {
    try { await api.post(`/hr/advances/${id}/reject`); toast.success("Acompte refusé"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const pay = async (id) => {
    if (!window.confirm("Marquer comme payé ? Une ligne de retenue sera injectée dans la paie du mois de retenue.")) return;
    try { await api.post(`/hr/advances/${id}/pay`); toast.success("Acompte marqué payé + retenue injectée"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Supprimer cette demande ?")) return;
    try { await api.delete(`/hr/advances/${id}`); toast.success("Demande supprimée"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const pending = rows.filter(r => r.status === "pending").length;
  const approved = rows.filter(r => r.status === "approved").length;
  const totalPaid = rows.filter(r => r.status === "paid").reduce((s, r) => s + Number(r.amount || 0), 0);

  return (
    <div className="space-y-6" data-testid="advances-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
            Acomptes
          </h1>
          <p className="text-sm text-slate-500">
            {rows.length} demande{rows.length > 1 ? "s" : ""} · {pending} à valider · {approved} à payer · {fmtEUR(totalPaid)} versés
          </p>
        </div>
        {canWrite && (
          <Button onClick={() => setOpen(true)} className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="new-advance-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Nouvelle demande
          </Button>
        )}
      </div>

      <Card>
        <CardContent className="p-4">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="max-w-xs" data-testid="filter-adv-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous statuts</SelectItem>
              <SelectItem value="pending">À valider</SelectItem>
              <SelectItem value="approved">Validés (à payer)</SelectItem>
              <SelectItem value="paid">Payés</SelectItem>
              <SelectItem value="rejected">Refusés</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-3">{[...Array(4)].map((_, i) =>
              <div key={i} className="h-10 bg-slate-100 animate-pulse rounded" />)}</div>
          ) : rows.length === 0 ? (
            <div className="p-12 text-center" data-testid="advances-empty">
              <Wallet className="w-12 h-12 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">Aucun acompte enregistré.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs uppercase text-slate-500">Salarié</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500 text-right">Montant</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500">Demandé le</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500">Retenue sur</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500">Motif</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500">Statut</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((a, i) => {
                  const st = STATUS[a.status] || STATUS.pending;
                  const Icon = st.icon;
                  return (
                    <motion.tr key={a.id}
                      initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: Math.min(i, 10) * 0.02, duration: 0.2 }}
                      className="border-b hover:bg-slate-50"
                      data-testid={`advance-row-${i}`}>
                      <TableCell>
                        <div className="font-medium text-slate-900 text-sm">{a.employee_name}</div>
                        <div className="text-xs text-slate-500">{a.job_title || "—"}</div>
                      </TableCell>
                      <TableCell className="text-right font-mono font-semibold">{fmtEUR(a.amount)}</TableCell>
                      <TableCell className="text-sm font-mono">
                        {a.requested_date ? new Date(a.requested_date).toLocaleDateString("fr-FR") : "—"}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="bg-slate-50 text-slate-700 border-slate-200 font-mono text-xs">
                          {fmtPeriodFR(a.deduction_period)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-slate-600 max-w-[240px] truncate" title={a.reason}>
                        {a.reason || "—"}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={st.cls}>
                          <Icon className="w-3 h-3 mr-1" />{st.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="inline-flex items-center gap-1">
                          {canWrite && a.status === "pending" && (
                            <>
                              <Button size="sm" variant="ghost" className="h-7 text-emerald-600"
                                onClick={() => approve(a.id)} data-testid={`approve-adv-${a.id}`}>
                                <Check className="w-4 h-4" />
                              </Button>
                              <Button size="sm" variant="ghost" className="h-7 text-red-600"
                                onClick={() => reject(a.id)} data-testid={`reject-adv-${a.id}`}>
                                <X className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                          {canWrite && a.status === "approved" && (
                            <Button size="sm" variant="outline"
                              className="h-7 text-xs bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100"
                              onClick={() => pay(a.id)} data-testid={`pay-adv-${a.id}`}>
                              <CircleDollarSign className="w-3.5 h-3.5 mr-1" />
                              Marquer payé
                            </Button>
                          )}
                          {canWrite && (
                            <Button size="sm" variant="ghost" className="h-7 text-slate-500"
                              onClick={() => remove(a.id)}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </motion.tr>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <NewAdvanceDialog open={open} onOpenChange={setOpen} onSaved={load} />
    </div>
  );
}
